<template>
  <div
    v-if="open"
    ref="rootEl"
    class="absolute bottom-full left-0 right-0 mb-2 z-20 rounded-lg border border-white/10 bg-[var(--surface-color,#0d0e12)]/95 backdrop-blur-md shadow-2xl overflow-hidden flex flex-col max-h-[320px]"
    @click.stop
  >
    <!-- Search input -->
    <div class="flex items-center gap-2 px-3 py-2 border-b border-white/5 shrink-0">
      <el-icon size="14" class="text-text-muted"><Search /></el-icon>
      <input
        ref="inputEl"
        v-model="query"
        type="text"
        class="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-text-dim"
        :placeholder="$t('chat.emojis.search_placeholder')"
        @keydown.esc.stop="close"
      />
      <button
        type="button"
        class="p-1 rounded hover:bg-white/5 text-text-muted hover:text-white"
        :aria-label="$t('chat.actions.close')"
        @click="close"
      >
        <el-icon size="12"><Close /></el-icon>
      </button>
    </div>

    <!-- Results -->
    <div class="flex-1 overflow-y-auto px-2 py-2 scrollbar-thin">
      <div v-if="loadingResults" class="flex justify-center py-4 text-text-dim text-xs">
        {{ $t('chat.emojis.searching') }}
      </div>

      <!-- Tenant catalog hits -->
      <template v-else>
        <div v-if="tenantHits.length > 0" class="mb-2">
          <div class="text-[10px] uppercase tracking-widest text-text-dim px-1 mb-1">
            {{ query ? $t('chat.emojis.tenant_matches') : $t('chat.emojis.most_used') }}
          </div>
          <div class="grid grid-cols-6 gap-1">
            <button
              v-for="e in tenantHits"
              :key="'t-' + e.id"
              type="button"
              class="relative aspect-square rounded-md hover:bg-white/5 flex items-center justify-center group"
              :title="':' + e.name + ':'"
              @click="onPickTenant(e)"
            >
              <img :src="e.url" :alt="e.name" class="max-w-[28px] max-h-[28px] object-contain" loading="lazy" />
              <span class="absolute inset-x-0 -bottom-0.5 text-[8px] text-text-dim truncate px-1 opacity-0 group-hover:opacity-100 pointer-events-none">
                {{ e.name }}
              </span>
            </button>
          </div>
        </div>

        <!-- Slackmojis search hits (fresh, not yet downloaded) -->
        <div v-if="slackHits.length > 0">
          <div class="text-[10px] uppercase tracking-widest text-text-dim px-1 mb-1 flex items-center gap-1">
            {{ $t('chat.emojis.slackmojis') }}
            <span class="text-[9px] text-text-dim normal-case tracking-normal">— {{ $t('chat.emojis.slackmojis_hint') }}</span>
          </div>
          <div class="grid grid-cols-6 gap-1">
            <button
              v-for="h in slackHits"
              :key="'s-' + h.id"
              type="button"
              class="relative aspect-square rounded-md hover:bg-white/5 flex items-center justify-center group disabled:opacity-40"
              :title="':' + h.name + ':'"
              :disabled="picking === h.id"
              @click="onPickSlack(h)"
            >
              <img :src="h.imageUrl" :alt="h.name" class="max-w-[28px] max-h-[28px] object-contain" loading="lazy" />
              <span v-if="picking === h.id" class="absolute inset-0 bg-black/50 rounded-md flex items-center justify-center text-[9px] text-white">
                …
              </span>
              <span v-else class="absolute inset-x-0 -bottom-0.5 text-[8px] text-text-dim truncate px-1 opacity-0 group-hover:opacity-100 pointer-events-none">
                {{ h.name }}
              </span>
            </button>
          </div>
        </div>

        <div
          v-if="!loadingResults && tenantHits.length === 0 && slackHits.length === 0"
          class="text-xs text-text-dim text-center py-4"
        >
          {{ query ? $t('chat.emojis.no_results', { q: query }) : $t('chat.emojis.empty') }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { Search, Close } from '@element-plus/icons-vue'
import { useCustomEmoji } from '~/composables/chat/useCustomEmoji'

/**
 * Emoji picker popover. Opens above the composer. The consumer
 * (ChatComposer) controls `open` via v-model and listens to `@pick` to get
 * the chosen emoji's shortcode, which it inserts at the caret.
 *
 * Results pane is a two-section stack:
 *   1. Tenant catalog matches (already downloaded, render instantly)
 *   2. Fresh slackmojis.com hits — clicking one downloads to our S3 and
 *      moves it into the tenant catalog
 */

const props = defineProps({
  modelValue: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue', 'pick'])

const { ensureLoaded, search, pick } = useCustomEmoji()

const open = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v)
})

const rootEl = ref(null)
const inputEl = ref(null)

const query = ref('')
const tenantHits = ref([])
const slackHits = ref([])
const loadingResults = ref(false)
const picking = ref(null)   // slackmojisId currently being downloaded

let searchDebounceTimer

async function runSearch(q) {
  loadingResults.value = true
  try {
    const res = await search(q)
    tenantHits.value = res.tenantHits || []
    slackHits.value = res.slackmojisHits || []
  } catch (err) {
    console.error('[chat:emojis:picker] search failed', err)
    tenantHits.value = []
    slackHits.value = []
  } finally {
    loadingResults.value = false
  }
}

watch(query, (q) => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => runSearch(q), 200)
})

watch(open, async (v) => {
  if (v) {
    await ensureLoaded()
    await runSearch(query.value)
    await nextTick()
    inputEl.value?.focus()
  }
})

function close() {
  open.value = false
}

function onPickTenant(e) {
  emit('pick', { name: e.name, url: e.url })
  close()
}

async function onPickSlack(hit) {
  if (picking.value) return
  picking.value = hit.id
  try {
    const entry = await pick(hit)
    emit('pick', { name: entry.name, url: entry.url })
    close()
  } catch (err) {
    console.error('[chat:emojis:picker] pick failed', err)
  } finally {
    picking.value = null
  }
}

// Click-outside to dismiss. We listen on the bubbling `click` phase so
// the composer's toggle-button can `@click.stop` to keep the picker open
// when the operator clicks the toggle a second time. Listening on
// `mousedown` (the previous behaviour) ran *before* the click handler,
// so the toggle would close the picker on mousedown and re-open it on
// click — net result: the toggle never closed it.
function onDocClick(e) {
  if (!open.value) return
  const root = rootEl.value
  if (!root) return
  if (root === e.target || root.contains(e.target)) return
  close()
}
onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))
</script>
